import React, { useState, useEffect } from 'react';
import './App.css';

const FutoshikiGame = () => {
  const [boardSize, setBoardSize] = useState(6);
  const [grid, setGrid] = useState([]);
  const [horizontalConstraints, setHorizontalConstraints] = useState([]);
  const [verticalConstraints, setVerticalConstraints] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);
  const [initialGrid, setInitialGrid] = useState([]);
  const [showCongrats, setShowCongrats] = useState(false);
  const [congratsMessage, setCongratsMessage] = useState('');

  // Initialize empty grid
  const createEmptyGrid = (size) => {
    return Array(size).fill().map(() => Array(size).fill(0));
  };

  // Initialize empty constraints
  const createEmptyConstraints = (size, isHorizontal = true) => {
    if (isHorizontal) {
      return Array(size).fill().map(() => Array(size - 1).fill(''));
    } else {
      return Array(size - 1).fill().map(() => Array(size).fill(''));
    }
  };

  // Generate a completely solvable puzzle using backtracking
  const generatePuzzle = (size) => {
    // Step 1: Generate a complete valid solution first
    const solution = generateCompleteSolution(size);
    if (!solution) return createSimplePuzzle(size);

    // Step 2: Create puzzle by removing numbers and adding constraints
    const puzzleGrid = solution.map(row => [...row]); // Start with complete solution
    
    // Remove random numbers to create the puzzle (keep 25-35%)
    const totalCells = size * size;
    const numbersToKeep = Math.floor(totalCells * (0.25 + Math.random() * 0.1));
    const cellsToRemove = totalCells - numbersToKeep;
    
    // Randomly remove cells
    const allPositions = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        allPositions.push([r, c]);
      }
    }
    
    // Shuffle positions and remove cells
    shuffleArray(allPositions);
    for (let i = 0; i < cellsToRemove; i++) {
      const [r, c] = allPositions[i];
      puzzleGrid[r][c] = 0;
    }

    // Step 3: Generate constraints based on the solution
    const { horizontal, vertical } = generateConstraintsFromSolution(solution, puzzleGrid, size);

    return {
      grid: puzzleGrid,
      horizontal,
      vertical,
      solution: solution
    };
  };

  // Generate a complete valid solution
  const generateCompleteSolution = (size) => {
    const grid = createEmptyGrid(size);
    
    // Fill the grid using backtracking with randomization
    const fillGrid = () => {
      for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
          if (grid[row][col] === 0) {
            // Create randomized number list
            const numbers = Array.from({ length: size }, (_, i) => i + 1);
            shuffleArray(numbers);
            
            for (const num of numbers) {
              if (isValidPlacement(grid, row, col, num, size)) {
                grid[row][col] = num;
                if (fillGrid()) return true;
                grid[row][col] = 0;
              }
            }
            return false;
          }
        }
      }
      return true;
    };

    return fillGrid() ? grid : null;
  };

  // Check if number placement is valid (no duplicates in row/column)
  const isValidPlacement = (grid, row, col, num, size) => {
    // Check row
    for (let c = 0; c < size; c++) {
      if (c !== col && grid[row][c] === num) return false;
    }
    
    // Check column
    for (let r = 0; r < size; r++) {
      if (r !== row && grid[r][col] === num) return false;
    }
    
    return true;
  };

  // Generate constraints based on the complete solution
  const generateConstraintsFromSolution = (solution, puzzleGrid, size) => {
    const horizontal = createEmptyConstraints(size, true);
    const vertical = createEmptyConstraints(size, false);
    
    // Generate horizontal constraints
    const maxHorizontalConstraints = Math.floor(size * (size - 1) * 0.3);
    let horizontalCount = 0;
    
    const horizontalPositions = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size - 1; c++) {
        // Don't place constraints between two initial numbers
        const leftHasValue = puzzleGrid[r][c] !== 0;
        const rightHasValue = puzzleGrid[r][c + 1] !== 0;
        if (!(leftHasValue && rightHasValue)) {
          horizontalPositions.push([r, c]);
        }
      }
    }
    
    shuffleArray(horizontalPositions);
    for (let i = 0; i < Math.min(maxHorizontalConstraints, horizontalPositions.length); i++) {
      const [r, c] = horizontalPositions[i];
      const leftValue = solution[r][c];
      const rightValue = solution[r][c + 1];
      
      if (leftValue !== rightValue) {
        horizontal[r][c] = leftValue < rightValue ? '<' : '>';
        horizontalCount++;
      }
    }

    // Generate vertical constraints
    const maxVerticalConstraints = Math.floor((size - 1) * size * 0.3);
    let verticalCount = 0;
    
    const verticalPositions = [];
    for (let r = 0; r < size - 1; r++) {
      for (let c = 0; c < size; c++) {
        // Don't place constraints between two initial numbers
        const topHasValue = puzzleGrid[r][c] !== 0;
        const bottomHasValue = puzzleGrid[r + 1][c] !== 0;
        if (!(topHasValue && bottomHasValue)) {
          verticalPositions.push([r, c]);
        }
      }
    }
    
    shuffleArray(verticalPositions);
    for (let i = 0; i < Math.min(maxVerticalConstraints, verticalPositions.length); i++) {
      const [r, c] = verticalPositions[i];
      const topValue = solution[r][c];
      const bottomValue = solution[r + 1][c];
      
      if (topValue !== bottomValue) {
        vertical[r][c] = topValue < bottomValue ? '^' : 'v';
        verticalCount++;
      }
    }

    return { horizontal, vertical };
  };

  // Shuffle array in place (Fisher-Yates algorithm)
  const shuffleArray = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  };

  // Create a simple puzzle as fallback
  const createSimplePuzzle = (size) => {
    const puzzleGrid = createEmptyGrid(size);
    const horizontal = createEmptyConstraints(size, true);
    const vertical = createEmptyConstraints(size, false);
    
    // Add a few numbers and constraints
    puzzleGrid[0][0] = 1;
    puzzleGrid[size - 1][size - 1] = size;
    if (size > 2) {
      puzzleGrid[Math.floor(size / 2)][Math.floor(size / 2)] = Math.floor(size / 2);
    }
    
    // Add some basic constraints
    if (size > 1) {
      horizontal[0][0] = '<';
      if (size > 2) {
        vertical[0][0] = '^';
      }
    }

    return {
      grid: puzzleGrid,
      horizontal,
      vertical,
      solution: null
    };
  };

  // Solve puzzle function
  const solvePuzzle = (initialGrid, hConstraints, vConstraints, size) => {
    const grid = initialGrid.map(row => [...row]);
    
    const isValid = (row, col, num) => {
      // Check row constraint
      for (let c = 0; c < size; c++) {
        if (c !== col && grid[row][c] === num) return false;
      }
      
      // Check column constraint
      for (let r = 0; r < size; r++) {
        if (r !== row && grid[r][col] === num) return false;
      }
      
      return true;
    };

    const checkConstraints = () => {
      // Check horizontal constraints
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size - 1; c++) {
          const constraint = hConstraints[r][c];
          if (constraint && grid[r][c] !== 0 && grid[r][c + 1] !== 0) {
            if (constraint === '<' && grid[r][c] >= grid[r][c + 1]) return false;
            if (constraint === '>' && grid[r][c] <= grid[r][c + 1]) return false;
          }
        }
      }
      
      // Check vertical constraints
      for (let r = 0; r < size - 1; r++) {
        for (let c = 0; c < size; c++) {
          const constraint = vConstraints[r][c];
          if (constraint && grid[r][c] !== 0 && grid[r + 1][c] !== 0) {
            if (constraint === '^' && grid[r][c] >= grid[r + 1][c]) return false;
            if (constraint === 'v' && grid[r][c] <= grid[r + 1][c]) return false;
          }
        }
      }
      
      return true;
    };

    const backtrack = () => {
      for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
          if (grid[row][col] === 0) {
            for (let num = 1; num <= size; num++) {
              if (isValid(row, col, num)) {
                grid[row][col] = num;
                if (checkConstraints() && backtrack()) {
                  return true;
                }
                grid[row][col] = 0;
              }
            }
            return false;
          }
        }
      }
      return true;
    };
    
    if (backtrack()) {
      return grid;
    }
    return null;
  };

  // Check for duplicate errors in row/column
  const getDuplicateErrors = () => {
    const errors = new Set();
    
    if (!grid || grid.length === 0) return errors;
    
    // Check rows for duplicates
    for (let r = 0; r < boardSize; r++) {
      if (!grid[r]) continue;
      const seen = new Map();
      for (let c = 0; c < boardSize; c++) {
        const val = grid[r][c];
        if (val !== 0) {
          if (seen.has(val)) {
            errors.add(`${r}-${c}`);
            errors.add(`${r}-${seen.get(val)}`);
          } else {
            seen.set(val, c);
          }
        }
      }
    }
    
    // Check columns for duplicates
    for (let c = 0; c < boardSize; c++) {
      const seen = new Map();
      for (let r = 0; r < boardSize; r++) {
        if (!grid[r]) continue;
        const val = grid[r][c];
        if (val !== 0) {
          if (seen.has(val)) {
            errors.add(`${r}-${c}`);
            errors.add(`${seen.get(val)}-${c}`);
          } else {
            seen.set(val, r);
          }
        }
      }
    }
    
    return errors;
  };

  // Check constraint violations
  const getConstraintErrors = () => {
    const horizontalErrors = [];
    const verticalErrors = [];
    
    if (!grid || grid.length === 0 || !horizontalConstraints || !verticalConstraints) {
      return { horizontalErrors, verticalErrors };
    }
    
    // Check horizontal constraints
    for (let r = 0; r < boardSize; r++) {
      if (!grid[r] || !horizontalConstraints[r]) continue;
      for (let c = 0; c < boardSize - 1; c++) {
        const constraint = horizontalConstraints[r][c];
        if (constraint && grid[r][c] !== 0 && grid[r][c + 1] !== 0) {
          if ((constraint === '<' && grid[r][c] >= grid[r][c + 1]) ||
              (constraint === '>' && grid[r][c] <= grid[r][c + 1])) {
            horizontalErrors.push(`${r}-${c}`);
          }
        }
      }
    }
    
    // Check vertical constraints
    for (let r = 0; r < boardSize - 1; r++) {
      if (!grid[r] || !grid[r + 1] || !verticalConstraints[r]) continue;
      for (let c = 0; c < boardSize; c++) {
        const constraint = verticalConstraints[r][c];
        if (constraint && grid[r][c] !== 0 && grid[r + 1][c] !== 0) {
          if ((constraint === '^' && grid[r][c] >= grid[r + 1][c]) ||
              (constraint === 'v' && grid[r][c] <= grid[r + 1][c])) {
            verticalErrors.push(`${r}-${c}`);
          }
        }
      }
    }
    
    return { horizontalErrors, verticalErrors };
  };

  // Start new game
  const startNewGame = () => {
    const puzzle = generatePuzzle(boardSize);
    setGrid(puzzle.grid);
    setInitialGrid(puzzle.grid.map(row => [...row]));
    setHorizontalConstraints(puzzle.horizontal);
    setVerticalConstraints(puzzle.vertical);
    setGameStarted(true);
  };

  // Refresh current board size
  const refreshBoard = () => {
    if (gameStarted) {
      startNewGame();
    }
  };

  // Update cell value
  const updateCell = (row, col, value) => {
    if (value < 0 || value > boardSize) return;
    // Don't allow changing initial numbers
    if (initialGrid && initialGrid[row] && initialGrid[row][col] !== 0) return;
    
    const newGrid = [...grid];
    newGrid[row][col] = value;
    setGrid(newGrid);
  };

  // Solve current puzzle
  const solveCurrent = () => {
    const solution = solvePuzzle(grid, horizontalConstraints, verticalConstraints, boardSize);
    if (solution) {
      setGrid(solution);
      // Show congratulations toast for solving
      setCongratsMessage("🧠 Puzzle Solved! Great job using the solver!");
      setShowCongrats(true);
    } else {
      alert('No solution found!');
    }
  };

  // Handle congratulations and start new game
  const handleNewGameFromCongrats = () => {
    setShowCongrats(false);
    startNewGame();
  };

  // Check if puzzle is complete and correct
  const isComplete = () => {
    if (!gameStarted) return false;
    
    // Check if all cells are filled
    const allFilled = grid.every(row => row.every(cell => cell !== 0));
    if (!allFilled) return false;

    // Check for no errors
    const duplicateErrors = getDuplicateErrors();
    const { horizontalErrors, verticalErrors } = getConstraintErrors();
    
    const isCompleteAndCorrect = duplicateErrors.size === 0 && horizontalErrors.length === 0 && verticalErrors.length === 0;
    
    // Show congratulations if just completed
    if (isCompleteAndCorrect && !showCongrats) {
      setCongratsMessage("🎉 Puzzle Complete! Amazing work solving it yourself!");
      setShowCongrats(true);
    }
    
    return isCompleteAndCorrect;
  };

  // Initialize on component mount
  useEffect(() => {
    if (!gameStarted) {
      setGrid(createEmptyGrid(boardSize));
      setInitialGrid(createEmptyGrid(boardSize));
      setHorizontalConstraints(createEmptyConstraints(boardSize, true));
      setVerticalConstraints(createEmptyConstraints(boardSize, false));
    }
  }, [boardSize, gameStarted]);

  // Get current errors and satisfied constraints
  const duplicateErrors = gameStarted && grid.length > 0 ? getDuplicateErrors() : new Set();
  const { horizontalErrors, verticalErrors } = gameStarted && grid.length > 0 ? getConstraintErrors() : { horizontalErrors: [], verticalErrors: [] };
  
  // Check completion status
  const puzzleComplete = isComplete();

  return (
    <div className="flex flex-col items-center p-6 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-gray-800">Futoshiki Game</h1>
      
      {/* Congratulations Toast */}
      {showCongrats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="text-6xl mb-4 animate-pulse">🎉</div>
            <h2 className="text-2xl font-bold text-green-600 mb-4">Congratulations!</h2>
            <p className="text-lg text-gray-700 mb-6">{congratsMessage}</p>
            <button
              onClick={handleNewGameFromCongrats}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-300 font-medium text-lg transform hover:scale-105"
            >
              🎮 New Puzzle
            </button>
          </div>
        </div>
      )}
      
      {!gameStarted ? (
        <div className="flex flex-col items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="text-lg font-medium">Board Size:</label>
            <select 
              value={boardSize} 
              onChange={(e) => setBoardSize(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              {[4, 5, 6, 7, 8, 9].map(size => (
                <option key={size} value={size}>{size}×{size}</option>
              ))}
            </select>
          </div>
          <button
            onClick={startNewGame}
            className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-lg font-medium"
          >
            Start New Game
          </button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4 mb-6">
            <span className="text-lg font-medium">Board Size: {boardSize}×{boardSize}</span>
            <button
              onClick={refreshBoard}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              New Puzzle
            </button>
            <button
              onClick={() => {
                setGameStarted(false);
                setGrid(createEmptyGrid(boardSize));
                setShowCongrats(false);
              }}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Change Size
            </button>
          </div>

          <div className="mb-6">
            <div className="p-6 bg-white rounded-lg shadow-lg">
              <div className="inline-block">
                {/* Build the grid row by row, alternating between cell rows and constraint rows */}
                {Array.from({ length: boardSize * 2 - 1 }, (_, index) => {
                  const isRowOfCells = index % 2 === 0;
                  const gridRowIndex = Math.floor(index / 2);
                  
                  if (isRowOfCells) {
                    // Row of cells with horizontal constraints between them
                    return (
                      <div key={index} className="flex items-center" style={{ height: '56px' }}>
                        {Array.from({ length: boardSize * 2 - 1 }, (_, cellIndex) => {
                          const isCellColumn = cellIndex % 2 === 0;
                          const gridColIndex = Math.floor(cellIndex / 2);
                          
                          if (isCellColumn) {
                            // Actual cell
                            const cell = grid[gridRowIndex] ? grid[gridRowIndex][gridColIndex] : 0;
                            return (
                              <input
                                key={cellIndex}
                                type="number"
                                min="0"
                                max={boardSize}
                                value={cell === 0 ? '' : cell}
                                onChange={(e) => updateCell(gridRowIndex, gridColIndex, parseInt(e.target.value) || 0)}
                                className={`w-14 h-14 text-center border-2 rounded text-lg font-bold transition-colors ${
                                  initialGrid[gridRowIndex] && initialGrid[gridRowIndex][gridColIndex] !== 0 
                                    ? 'bg-blue-100 border-blue-400 cursor-not-allowed' 
                                    : duplicateErrors.has(`${gridRowIndex}-${gridColIndex}`)
                                    ? 'bg-red-100 border-red-400'
                                    : 'border-gray-300 hover:border-gray-400'
                                }`}
                                readOnly={initialGrid[gridRowIndex] && initialGrid[gridRowIndex][gridColIndex] !== 0}
                                style={{ width: '56px', height: '56px' }}
                              />
                            );
                          } else {
                            // Horizontal constraint between cells
                            const constraint = horizontalConstraints[gridRowIndex] && horizontalConstraints[gridRowIndex][gridColIndex] ? horizontalConstraints[gridRowIndex][gridColIndex] : '';
                            return (
                              <div key={cellIndex} className="flex items-center justify-center" style={{ width: '32px', height: '56px' }}>
                                {constraint && (
                                  <span className={`font-bold text-2xl transition-colors ${
                                    horizontalErrors.includes(`${gridRowIndex}-${gridColIndex}`)
                                      ? 'text-red-500'
                                      : 'text-blue-600'
                                  }`}>
                                    {constraint}
                                  </span>
                                )}
                              </div>
                            );
                          }
                        })}
                      </div>
                    );
                  } else {
                    // Row of vertical constraints
                    return (
                      <div key={index} className="flex items-center" style={{ height: '32px' }}>
                        {Array.from({ length: boardSize * 2 - 1 }, (_, cellIndex) => {
                          const isCellColumn = cellIndex % 2 === 0;
                          const gridColIndex = Math.floor(cellIndex / 2);
                          
                          if (isCellColumn) {
                            // Vertical constraint
                            const constraint = verticalConstraints[gridRowIndex] && verticalConstraints[gridRowIndex][gridColIndex] ? verticalConstraints[gridRowIndex][gridColIndex] : '';
                            return (
                              <div key={cellIndex} className="flex items-center justify-center" style={{ width: '56px', height: '32px' }}>
                                {constraint && (
                                  <span className={`font-bold text-2xl transition-colors ${
                                    verticalErrors.includes(`${gridRowIndex}-${gridColIndex}`)
                                      ? 'text-red-500'
                                      : 'text-blue-600'
                                  }`}>
                                    {constraint}
                                  </span>
                                )}
                              </div>
                            );
                          } else {
                            // Empty space (intersection of constraint lines)
                            return (
                              <div key={cellIndex} style={{ width: '32px', height: '32px' }}></div>
                            );
                          }
                        })}
                      </div>
                    );
                  }
                })}
              </div>
            </div>
          </div>
          
          <div className="flex gap-4 mb-4">
            <button
              onClick={solveCurrent}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Solve
            </button>
          </div>
        </>
      )}
      
      <div className="mt-6 max-w-md text-center text-gray-600">
        <h3 className="font-bold mb-2">Rules:</h3>
        <ul className="text-sm space-y-1">
          <li>• Fill each row and column with numbers 1-{boardSize}</li>
          <li>• No duplicates in any row or column</li>
          <li>• Follow the inequality constraints ({"<"}, {">"}, ^, v)</li>
          <li>• <span className="text-blue-400">Blue cells</span> are given numbers</li>
          <li>• <span className="text-red-400">Red highlighting</span> shows rule violations</li>
        </ul>
      </div>
    </div>
  );
};

function App() {
  return <FutoshikiGame />;
}

export default App;